import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../context/AuthContext';
import { publicAPI, paymentAPI } from '../../services/api';
import { Badge, Button } from '../../components/UI';
import { calculateGST } from '../../utils/gst';
import SeatLimitBanner from '../../components/SeatLimitBanner';
import {
  HiCheck,
  HiStar,
  HiLightningBolt,
  HiShieldCheck,
  HiMinusSm,
  HiPlusSm,
  HiExclamation,
  HiClock,
  HiDownload,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

// Lazy-load Razorpay checkout SDK
let razorpayLoadPromise = null;
function loadRazorpaySDK() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayLoadPromise) return razorpayLoadPromise;
  razorpayLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { razorpayLoadPromise = null; reject(new Error('Failed to load Razorpay SDK')); };
    document.body.appendChild(script);
  });
  return razorpayLoadPromise;
}

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

// Fallback pricing if server data is not yet loaded
const FALLBACK_PRICE_PER_USER = { monthly: 599, yearly: 299 };

const CYCLES = [
  { id: 'monthly', label: 'Monthly', backend: 'monthly' },
  { id: 'yearly', label: 'Yearly', backend: 'yearly' },
];

// Fallback feature lists — only used before server plans load
const FALLBACK_FULL_ACCESS_FEATURES = [
  'SIM Based Calling & Auto Dialer',
  'Call Recording',
  'Unlimited Lead Management',
  'Follow-Up Reminders',
  'Advanced Reports & Analytics',
  'Notes & Activity Tracking',
  'Team Performance Tracking',
  'Lead Distribution System',
  'Manager Dashboard & Monitoring',
  'WhatsApp & Email Integration',
  'Google Sheet Integration',
  'Mobile App Access',
  'Priority Support',
  'Data Export & Audit Logs',
];

const FALLBACK_FREE_FEATURES = [
  'SIM Based Calling',
  'Lead Management (up to 150)',
  'Follow-Up Reminders',
  'Basic Reports',
  'Notes & Activity Tracking',
  'Mobile App Access',
];

// Feature flag labels for dynamic plan display
const FEATURE_LABELS = {
  callRecording: 'Call Recording',
  analytics: 'Reports & Analytics',
  advancedAnalytics: 'Advanced Reports & Analytics',
  api: 'API Access',
  webhooks: 'Webhooks',
  customBranding: 'Custom Branding',
  prioritySupport: 'Priority Support',
  sso: 'Single Sign-On (SSO)',
  auditLogs: 'Data Export & Audit Logs',
  whatsappIntegration: 'WhatsApp Integration',
  emailIntegration: 'Email Integration',
  mobileApp: 'Mobile App Access',
  exportData: 'Data Export',
  googleSheetIntegration: 'Google Sheet Integration',
};

// Build a human-readable feature list from a plan object
const getPlanFeatureLabels = (plan) => {
  if (!plan) return [];
  const features = [];
  // Baseline features every plan has
  features.push('SIM Based Calling');
  if (plan.limits?.contacts === -1) {
    features.push('Unlimited Lead Management');
  } else if (plan.limits?.contacts) {
    features.push(`Lead Management (up to ${plan.limits.contacts})`);
  } else {
    features.push('Lead Management');
  }
  features.push('Follow-Up Reminders');
  features.push('Notes & Activity Tracking');
  features.push('Mobile App Access');
  // Add features from the plan's feature flags
  if (plan.features && typeof plan.features === 'object') {
    Object.entries(plan.features).forEach(([key, enabled]) => {
      if (enabled && FEATURE_LABELS[key]) {
        features.push(FEATURE_LABELS[key]);
      }
    });
  }
  return [...new Set(features)];
};

// Breakdown of a payment-history record (plan price → promo → taxable → GST → total)
const toNum = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};
const getPaymentBreakdown = (payment) => {
  const notes = payment?.notes || {};
  const originalAmount = toNum(notes.originalAmount);
  const discountAmount = toNum(notes.discountAmount) || 0;
  const taxableAmount = toNum(payment?.amount);
  const gstAmount = toNum(payment?.gstAmount) || 0;
  const cgstAmount = toNum(payment?.cgstAmount) || 0;
  const sgstAmount = toNum(payment?.sgstAmount) || 0;
  return {
    originalAmount,
    discountAmount,
    promoCode: notes.promoCode || null,
    taxableAmount,
    gstAmount,
    cgstAmount,
    sgstAmount,
    gstType: notes.gstType || null,
    hasDetails: discountAmount > 0 || gstAmount > 0 || cgstAmount > 0 || sgstAmount > 0 || originalAmount != null,
  };
};

const Subscription = () => {
  const navigate = useNavigate();
  const { organization, plan, isExpired, daysRemaining, isTrial, subscriptionStartsAt, subscriptionEndsAt, billingCycle, refreshOrg, pendingSeatsChange, pendingDowngradePlan, pendingBillingCycle, autopay } = useOrg();
  const { switchOrganization } = useAuth();
  const [cycleId, setCycleId] = useState('monthly');
  const [numUsers, setNumUsers] = useState(1);
  const [loading, setLoading] = useState(true);
  const [serverPlans, setServerPlans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCycleSwitchConfirm, setShowCycleSwitchConfirm] = useState(false);
  const [pendingCycleSwitch, setPendingCycleSwitch] = useState(null);
  const [autopayProcessing, setAutopayProcessing] = useState(false);

  // Refer & Earn coin redemption (seat upgrade only) — all-or-nothing: toggling
  // it on always redeems the full usable amount, no partial selection.
  const [useCoins, setUseCoins] = useState(false);

  const currentPlanSlug = plan?.slug;
  const cycle = CYCLES.find((c) => c.id === cycleId);

  // Current paid seats from org
  const currentPaidSeats = organization?.paidSeats || 1;

  // Find plan objects from server data (must be before derived values that reference them)
  const fullAccessPlan = useMemo(() => {
    return serverPlans.find((p) => p.slug === 'full-access' || p.isPerUser);
  }, [serverPlans]);

  const freePlan = useMemo(() => {
    return serverPlans.find((p) => p.slug === 'free');
  }, [serverPlans]);

  // Determine the effective plan — use org plan, fallback to server plans or subscription history
  const activeSub = paymentHistory?.[0];
  const activeSubPlanSlug = activeSub?.planId?.slug || activeSub?.notes?.planSlug;
  const activeSubBilling = activeSub?.billingCycle || organization?.billingCycle;

  const effectivePlan = plan
    || (currentPlanSlug ? serverPlans.find(p => p.slug === currentPlanSlug) : null)
    || (activeSubPlanSlug ? serverPlans.find(p => p.slug === activeSubPlanSlug) : null)
    || freePlan
    || serverPlans.find(p => p.slug === 'free');

  // derived current plan display info
  const effectiveSlug = effectivePlan?.slug || currentPlanSlug || activeSubPlanSlug;

  // Effective billing cycle — subscription data is most authoritative, then org data
  const effectiveBillingCycle = activeSubBilling || billingCycle || 'monthly';

  // Sync numUsers with currentPaidSeats when on full-access plan
  useEffect(() => {
    if (effectiveSlug === 'full-access' && currentPaidSeats >= 1) {
      setNumUsers(prev => {
        // Only bump up, never reduce user's manual selection
        if (prev < currentPaidSeats) return currentPaidSeats;
        return prev;
      });
    }
  }, [effectiveSlug, currentPaidSeats]);

  // Lock billing cycle to the current cycle when on full-access plan
  useEffect(() => {
    if (effectiveSlug === 'full-access' && effectiveBillingCycle) {
      setCycleId(effectiveBillingCycle);
    }
  }, [effectiveSlug, effectiveBillingCycle]);

  const currentPlanName = effectivePlan?.name
    || (effectiveSlug === 'free' ? (freePlan?.name || 'Free Plan')
      : effectiveSlug === 'full-access' ? (fullAccessPlan?.name || 'Professional')
      : (effectiveSlug === 'trial' || effectiveSlug === '3-day-trial') ? 'Trial'
      : effectiveSlug || '—');

  const currentPlanPrice = effectiveSlug === 'free'
    ? (freePlan ? `₹${(freePlan.price?.monthly || 0).toLocaleString('en-IN')} forever` : '₹0 forever')
    : effectivePlan?.isPerUser
      ? `₹${(effectivePlan?.pricePerUser?.[effectiveBillingCycle] || effectivePlan?.pricePerUser?.monthly || 599).toLocaleString('en-IN')}/user/month`
      : `₹${(effectivePlan?.price?.[effectiveBillingCycle] || effectivePlan?.price?.monthly || 0).toLocaleString('en-IN')}/month`;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await publicAPI.getPlans();
        const plansData = res.data?.data || res.data || [];
        setServerPlans(plansData);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Fetch payment history
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await paymentAPI.getHistory({ page: 1, limit: 10 });
      setPaymentHistory(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Helper: comparable tier value for plan ordering
  const getPlanTier = (p) => {
    if (!p) return 0;
    if (p.displayOrder !== undefined && p.displayOrder !== null) return p.displayOrder;
    if (p.slug === 'free') return 0;
    if (p.slug === 'trial') return 1;
    return (p.isPerUser ? (p.pricePerUser?.monthly || 0) : (p.price?.monthly || 0)) + 2;
  };

  // Trial plans (₹0 with trialDays) are only chosen at signup — never offered
  // as an upgrade, so a trial can't be extended by switching to another trial.
  const isTrialPlanData = (p) =>
    !!p && !p.isPerUser && !(p.price?.monthly > 0) && !(p.price?.yearly > 0) && p.trialDays > 0;

  // Plans available for upgrade (higher tier than current)
  const upgradePlans = useMemo(() => {
    const currentTier = getPlanTier(effectivePlan);
    return serverPlans
      .filter((p) => !isTrialPlanData(p))
      .filter((p) => getPlanTier(p) > currentTier)
      .sort((a, b) => getPlanTier(a) - getPlanTier(b));
  }, [serverPlans, effectivePlan]);

  // Calculate per-user pricing from server plan data
  const pricePerUserMonthly = fullAccessPlan?.pricePerUser?.monthly || FALLBACK_PRICE_PER_USER.monthly;
  const pricePerUserYearly = fullAccessPlan?.pricePerUser?.yearly || FALLBACK_PRICE_PER_USER.yearly;
  const perUserPrice = cycleId === 'yearly' ? pricePerUserYearly : pricePerUserMonthly;
  const subtotal = perUserPrice * numUsers * (cycleId === 'yearly' ? 12 : 1);
  const planGstConfig = fullAccessPlan?.gstConfig || 'applicable';
  const orgState = organization?.address?.state || null;
  const { gstAmount, cgstAmount, sgstAmount, totalAmount, gstType } = calculateGST(subtotal, planGstConfig, orgState);
  const yearlySavings = (pricePerUserMonthly - pricePerUserYearly) * 12 * numUsers;

  // --- Seat upgrade derived values ---
  const additionalUsers = Math.max(0, numUsers - currentPaidSeats);
  const isUpgradeMode = effectiveSlug === 'full-access' && additionalUsers > 0;
  const isDowngradeMode = effectiveSlug === 'full-access' && numUsers < currentPaidSeats;
  const minUsers = 1;

  // Remaining months in current billing period (for yearly proration)
  const upgradeRemainingMonths = useMemo(() => {
    if (!isUpgradeMode || cycleId !== 'yearly') return 1;
    if (!organization?.subscriptionEndsAt) return 12;
    const diffMs = new Date(organization.subscriptionEndsAt).getTime() - Date.now();
    return Math.max(1, Math.min(12, Math.ceil(diffMs / (30 * 24 * 60 * 60 * 1000))));
  }, [isUpgradeMode, cycleId, organization?.subscriptionEndsAt]);

  const upgradeSubtotal = isUpgradeMode
    ? perUserPrice * additionalUsers * (cycleId === 'yearly' ? upgradeRemainingMonths : 1)
    : 0;

  // Refer & Earn: coins can only offset the seat-upgrade subtotal
  const coinValueInRupees = organization?.referAndEarn?.coinValueInRupees || 1;
  const coinBalance = organization?.coinBalance || 0;
  const maxRedeemableCoins = isUpgradeMode ? Math.min(coinBalance, Math.floor(upgradeSubtotal / coinValueInRupees)) : 0;
  // All-or-nothing — toggling "Use my coins" on always redeems the full usable amount.
  const appliedCoins = useCoins ? maxRedeemableCoins : 0;
  const coinDiscountAmount = Math.round(appliedCoins * coinValueInRupees * 100) / 100;
  const upgradeDiscountedSubtotal = Math.round((upgradeSubtotal - coinDiscountAmount) * 100) / 100;

  const upgradeGstCalc = calculateGST(upgradeDiscountedSubtotal, planGstConfig, orgState);
  const upgradeGstAmount = upgradeGstCalc.gstAmount;
  const upgradeCgstAmount = upgradeGstCalc.cgstAmount;
  const upgradeSgstAmount = upgradeGstCalc.sgstAmount;
  const upgradeTotalAmount = upgradeGstCalc.totalAmount;

  // Handle downgrade to free plan (scheduled or immediate depending on subscription status)
  const handleDowngrade = async () => {
    try {
      const res = await paymentAPI.downgrade();
      if (res.data?.success) {
        if (res.data.effectiveImmediately) {
          // Immediate downgrade (subscription already expired or never started)
          toast.success(`Downgraded to ${freePlan?.name || 'Free'} plan`);
          setNumUsers(1);
          setCycleId('monthly');
        } else {
          // Scheduled downgrade — access continues until end of billing period
          const downgradeDate = res.data.downgradeDate
            ? new Date(res.data.downgradeDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
            : 'your next renewal date';
          toast.success(res.data.message || `Downgrade scheduled. You'll keep Full Access until ${downgradeDate}.`);
        }
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
        setShowDowngradeConfirm(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to downgrade plan');
    }
  };

  // Handle cancelling a pending plan downgrade
  const handleCancelPlanDowngrade = async () => {
    setProcessing(true);
    try {
      const res = await paymentAPI.cancelDowngrade();
      if (res.data?.success) {
        toast.success(res.data.message || 'Plan downgrade cancelled');
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel downgrade');
    } finally {
      setProcessing(false);
    }
  };

  // Handle starting a trial
  const handleStartTrial = async () => {
    const trialPlan = serverPlans.find((p) => p.slug === 'trial');
    if (!trialPlan) {
      toast.error('Trial plan not available. Please contact support.');
      return;
    }

    setProcessing(true);
    try {
      await loadRazorpaySDK();

      const planId = trialPlan._id || trialPlan.slug;

      // Trial is free — just activate via verify with no payment
      // For trial plans with 0 cost, we can directly assign
      const { data: order } = await paymentAPI.createOrder({
        planId,
        billingCycle: 'monthly',
        numUsers: numUsers || 1,
      });

      // If trial is free, the create-order will return an error about free plans
      // In that case, we need a different flow — assign the trial directly
      // This should go through a different endpoint for free trial activation
      // For now, if createOrder fails for trial, show a message
      toast.info('Starting your free trial...');
      await refreshOrg();
      await fetchHistory();
    } catch (err) {
      console.error('Trial start error:', err);
      // Free trial might need direct activation without payment
      toast.error('Unable to start trial. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle seat upgrade for full-access plan
  const handleSeatUpgrade = async () => {
    if (!additionalUsers || additionalUsers < 1) return;
    setProcessing(true);
    try {
      await loadRazorpaySDK();

      const { data: order } = await paymentAPI.upgradeSeats({
        additionalUsers,
        billingCycle: cycleId,
        coinsToRedeem: appliedCoins || undefined,
      });

      const options = {
        key: order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID || '',
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'TeleValley',
        description: `Upgrade: +${order.additionalUsers} telecaller${order.additionalUsers > 1 ? 's' : ''}`,
        order_id: order.orderId,
        handler: async function (response) {
          try {
            const verifyRes = await paymentAPI.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              numUsers: order.newPaidSeats,
            });
            if (verifyRes.data?.success) {
              toast.success(`Upgraded to ${order.newPaidSeats} users successfully!`);
              setNumUsers(order.newPaidSeats);
              setUseCoins(false);
              await refreshOrg();
              await fetchHistory();
              window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
          } catch (verifyErr) {
            console.error('Seat upgrade verify error:', verifyErr);
            toast.error(verifyErr.response?.data?.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: organization?.name || '',
          email: '', // filled by Razorpay
        },
        theme: { color: '#4F46E5' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Seat upgrade error:', err);
      toast.error(err.response?.data?.message || 'Failed to initiate seat upgrade');
    } finally {
      setProcessing(false);
    }
  };

  // Handle scheduling a seat downgrade (takes effect at next renewal)
  const handleScheduleDowngrade = async () => {
    if (!numUsers || numUsers >= currentPaidSeats) return;
    setProcessing(true);
    try {
      const res = await paymentAPI.scheduleSeatDowngrade({ targetSeats: numUsers });
      if (res.data?.success) {
        toast.success(res.data.message || `Seat downgrade scheduled: ${currentPaidSeats} → ${numUsers} telecallers at next renewal`);
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule downgrade');
    } finally {
      setProcessing(false);
    }
  };

  // Handle cancelling a pending seat downgrade
  const handleCancelDowngrade = async () => {
    setProcessing(true);
    try {
      const res = await paymentAPI.cancelSeatDowngrade();
      if (res.data?.success) {
        toast.success(res.data.message || 'Downgrade cancelled');
        setNumUsers(currentPaidSeats);
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel downgrade');
    } finally {
      setProcessing(false);
    }
  };

  // Handle switching billing cycle (e.g. monthly → yearly) — scheduled for next renewal, no payment
  const handleCycleSwitch = async () => {
    if (!pendingCycleSwitch) return;
    setProcessing(true);
    setShowCycleSwitchConfirm(false);

    try {
      const res = await paymentAPI.switchCycle({ billingCycle: pendingCycleSwitch });
      if (res.data?.success) {
        toast.success(res.data.message || `Billing cycle change scheduled for your next renewal`);
        setPendingCycleSwitch(null);
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule billing cycle change');
    } finally {
      setProcessing(false);
    }
  };

  // Handle cancelling a pending billing cycle switch
  const handleCancelCycleSwitch = async () => {
    setProcessing(true);
    try {
      const res = await paymentAPI.cancelCycleSwitch();
      if (res.data?.success) {
        toast.success(res.data.message || 'Billing cycle change cancelled');
        await refreshOrg();
        await fetchHistory();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel billing cycle change');
    } finally {
      setProcessing(false);
    }
  };

  // Handle enabling (or re-enabling, after a halt) recurring auto-pay
  const handleEnableAutopay = async () => {
    setAutopayProcessing(true);
    try {
      await loadRazorpaySDK();
      const { data: setup } = await paymentAPI.enableAutopay();

      const options = {
        key: setup.keyId,
        subscription_id: setup.subscriptionId,
        name: 'TeleValley',
        description: `Auto-Pay — ${setup.planName} · ${setup.seats} seat${setup.seats > 1 ? 's' : ''} · ${setup.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`,
        handler: async (response) => {
          try {
            const verifyRes = await paymentAPI.verifyAutopay({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data?.success) {
              toast.success('Auto-pay enabled! Future renewals will be charged automatically.');
              await refreshOrg();
            }
          } catch (verifyErr) {
            console.error('Autopay verify error:', verifyErr);
            toast.error(verifyErr.response?.data?.message || 'Could not confirm auto-pay. Please try again.');
          }
        },
        prefill: {
          name: organization?.name || '',
          email: organization?.email || '',
        },
        theme: { color: '#064ee6' },
        modal: {
          ondismiss: () => {
            toast('Auto-pay setup cancelled', { icon: 'ℹ️' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Enable autopay error:', err);
      toast.error(err.response?.data?.message || 'Failed to set up auto-pay. Please try again.');
    } finally {
      setAutopayProcessing(false);
    }
  };

  // Handle disabling auto-pay (current period keeps running; just won't renew automatically)
  const handleCancelAutopay = async () => {
    if (!window.confirm("Disable auto-pay? Your current subscription period continues until it ends, but it won't renew automatically after that.")) return;
    setAutopayProcessing(true);
    try {
      const res = await paymentAPI.cancelAutopay();
      if (res.data?.success) {
        toast.success('Auto-pay disabled');
        await refreshOrg();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disable auto-pay');
    } finally {
      setAutopayProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Linked Workspaces (docs/LINKED_WORKSPACES_PLAN.md): this org is a workspace
          under another org's subscription — billing lives with the parent.
          DISABLED for now, feature planned for later. Commented out, not
          removed, per explicit request.
      {organization?.billingParentOrganization && (
        <div className="rounded-xl border border-primary-200 bg-primary-50 dark:bg-primary-500/10 dark:border-primary-500/30 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-primary-800 dark:text-primary-200">
            This workspace is billed via <strong>{organization.billingParentOrganization.name}</strong>.
            Any upgrade or renewal made here applies to that organization's subscription.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => switchOrganization(organization.billingParentOrganization._id)}
          >
            Switch to {organization.billingParentOrganization.name}
          </Button>
        </div>
      )}
      */}

      {/* Over-seat-limit warning (per-user plans) */}
      <SeatLimitBanner />

      {/* Current Plan Status */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-gray-900">Current Plan</h3>
              {isExpired ? (
                <Badge variant="danger">Expired</Badge>
              ) : isTrial ? (
                <Badge variant="warning">Trial</Badge>
              ) : (
                <Badge variant="success">Active</Badge>
              )}
            </div>
            <p className="text-base font-semibold text-gray-900 mt-2">
              {currentPlanName}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {currentPlanPrice}
              {effectiveBillingCycle && effectiveSlug !== 'free' && (
                <span className="text-gray-400">
                  {' '}· {effectiveBillingCycle === 'yearly' ? 'Yearly' : 'Monthly'} billing
                </span>
              )}
            </p>
            {effectiveSlug !== 'free' && currentPaidSeats > 0 && (
              <p className="text-sm text-gray-500 mt-0.5">
                {currentPaidSeats} telecaller{currentPaidSeats > 1 ? 's' : ''} + 1 admin = {currentPaidSeats + 1} total users
              </p>
            )}
            {isTrial && daysRemaining > 0 && (
              <p className="mt-1 text-sm text-amber-600 font-medium">
                {daysRemaining} trial day{daysRemaining !== 1 ? 's' : ''} remaining
              </p>
            )}
            {(effectiveSlug !== 'free' || (effectiveSlug === 'free' && organization?.subscriptionEndsAt)) && (subscriptionStartsAt || subscriptionEndsAt) && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {subscriptionStartsAt && (
                  <span>Started: {new Date(subscriptionStartsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                )}
                {subscriptionEndsAt && (
                  <span>{isExpired ? 'Expired' : 'Renews'}: {new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                )}
              </div>
            )}
            {effectivePlan?.description && (
              <p className="mt-2 text-sm text-gray-500">{effectivePlan.description}</p>
            )}
          </div>
          {upgradePlans.length > 0 && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="self-start sm:self-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors whitespace-nowrap"
            >
              Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Auto-Pay */}
      {effectiveSlug !== 'free' && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">Auto-Pay</h3>
                {autopay?.status === 'active' && <Badge variant="success">Active</Badge>}
                {autopay?.status === 'halted' && <Badge variant="danger">Action Needed</Badge>}
                {autopay?.status === 'pending_authorization' && <Badge variant="warning">Pending</Badge>}
              </div>
              {autopay?.status === 'active' ? (
                <p className="text-sm text-gray-500 mt-1">
                  Your subscription renews automatically
                  {autopay?.nextChargeAt && ` — next charge ~${new Date(autopay.nextChargeAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}.
                </p>
              ) : autopay?.status === 'halted' ? (
                <p className="text-sm text-amber-600 mt-1">
                  Your last automatic charge failed{autopay?.failureReason ? ` — ${autopay.failureReason}` : ''}. Pay manually to continue, or re-enable auto-pay.
                </p>
              ) : (
                <p className="text-sm text-gray-500 mt-1">
                  Turn on auto-pay so your subscription renews automatically each {effectiveBillingCycle === 'yearly' ? 'year' : 'month'} — no manual action needed.
                </p>
              )}
            </div>
            {autopay?.status === 'active' ? (
              <Button variant="outline" size="sm" onClick={handleCancelAutopay} disabled={autopayProcessing}>
                Disable Auto-Pay
              </Button>
            ) : (
              <Button size="sm" onClick={handleEnableAutopay} disabled={autopayProcessing}>
                {autopayProcessing ? 'Processing...' : autopay?.status === 'halted' ? 'Re-enable Auto-Pay' : 'Enable Auto-Pay'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Pending Seat Downgrade Banner */}
      {pendingSeatsChange && pendingSeatsChange < currentPaidSeats && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <HiExclamation className="h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Seat downgrade scheduled: {currentPaidSeats} → {pendingSeatsChange} telecaller{pendingSeatsChange > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-600">
                Takes effect at next renewal{subscriptionEndsAt ? ` on ${new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
                {' '}Make sure to deactivate excess telecallers before then.
              </p>
            </div>
          </div>
          <button
            onClick={handleCancelDowngrade}
            disabled={processing}
            className="shrink-0 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Pending Plan Downgrade Banner */}
      {pendingDowngradePlan && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <HiExclamation className="h-5 w-5 shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                Plan downgrade scheduled: Full Access → Free
              </p>
              <p className="text-xs text-red-600">
                Your Full Access features will continue until {subscriptionEndsAt ? new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'your next renewal date'}.
                {' '}Then your plan will switch to Free.
              </p>
            </div>
          </div>
          <button
            onClick={handleCancelPlanDowngrade}
            disabled={processing}
            className="shrink-0 rounded-lg border border-red-400 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Pending Billing Cycle Switch Banner */}
      {pendingBillingCycle && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-300 bg-blue-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <HiClock className="h-5 w-5 shrink-0 text-blue-600" />
            <div>
              <p className="text-sm font-semibold text-blue-800">
                Billing cycle change scheduled: {effectiveBillingCycle === 'yearly' ? 'Yearly' : 'Monthly'} → {pendingBillingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
              </p>
              <p className="text-xs text-blue-600">
                Takes effect at your next renewal{subscriptionEndsAt ? ` on ${new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
                {' '}Until then, your current {effectiveBillingCycle === 'yearly' ? 'yearly' : 'monthly'} billing continues unchanged.
              </p>
            </div>
          </div>
          <button
            onClick={handleCancelCycleSwitch}
            disabled={processing}
            className="shrink-0 rounded-lg border border-blue-400 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Savings Banner — hidden in upgrade/downgrade mode */}
      {cycleId === 'yearly' && !isUpgradeMode && !isDowngradeMode && (
        <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
          <HiStar className="h-5 w-5 shrink-0 text-green-600" />
          <p className="text-sm font-medium text-gray-700">
            <span className="font-bold text-green-600">You're saving {inr(yearlySavings)}/year</span>
            {' — '}Yearly billing at just {inr(pricePerUserYearly)}/user/month instead of {inr(pricePerUserMonthly)}/user/month
          </p>
        </div>
      )}

      {/* Full Access Plan Card */}
      <div className="relative overflow-hidden rounded-xl border-2 border-primary-400 bg-gradient-to-br from-white via-primary-50/30 to-white shadow-lg
        dark:border-primary-500/70 dark:bg-none dark:bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.40),rgba(49,46,129,0.92)_52%,rgba(2,6,23,0.98))]
        dark:!shadow-[0_0_55px_-12px_rgba(129,140,248,0.45)]">
        {/* Ambient glow blobs (visible mainly in dark theme) */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary-200/40 blur-3xl dark:bg-primary-500/30" />
        <div className="pointer-events-none absolute -bottom-28 -left-20 h-56 w-56 rounded-full bg-primary-300/30 blur-3xl dark:bg-violet-500/20" />

        {/* Popular badge */}
        <div className="absolute -top-0 right-6">
          <div className="rounded-b-lg bg-primary-600 px-4 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-md
            dark:bg-gradient-to-r dark:from-primary-500 dark:!to-violet-500 dark:!shadow-[0_2px_12px_rgba(99,102,241,0.6)]">
            <HiStar className="inline h-3 w-3 mr-1 -mt-0.5" />
            Best Value
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Plan info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{fullAccessPlan?.name || 'Full Access Plan'}</h2>
              <p className="mt-1 text-sm text-gray-500">{fullAccessPlan?.description || 'All features unlocked · Per-user pricing · Cancel anytime'}</p>

              {/* Current subscription details — shown when already on full-access */}
              {effectiveSlug === 'full-access' && (
                <div className="mt-3 rounded-lg bg-primary-50 border border-primary-100 px-4 py-3 text-sm
                  dark:bg-primary-500/10 dark:border-primary-400/30">
                  <div className="flex items-center gap-2">
                    <HiShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-300" />
                    <span className="font-semibold text-primary-700 dark:text-primary-300">
                      {currentPaidSeats} telecaller{currentPaidSeats > 1 ? 's' : ''} + 1 admin = {currentPaidSeats + 1} total users
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-primary-600 dark:text-primary-300/80">
                    <span>₹{perUserPrice.toLocaleString('en-IN')}/user/{effectiveBillingCycle === 'yearly' ? 'mo (yearly)' : 'month'}</span>
                    {subscriptionStartsAt && (
                      <span>Started: {new Date(subscriptionStartsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    )}
                    {subscriptionEndsAt && (
                      <span>Renews: {new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Billing Cycle Toggle */}
              <div className="mt-5 flex items-center gap-3">
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                  {CYCLES.map((c) => {
                    const isCurrentCycle = effectiveSlug === 'full-access' && effectiveBillingCycle === c.id;
                    const isPendingTarget = pendingBillingCycle === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          if (isCurrentCycle && !pendingBillingCycle) return; // already on this cycle, no pending change
                          if (isCurrentCycle && pendingBillingCycle) return; // current cycle, pending switch away
                          if (isPendingTarget) return; // already pending this cycle
                          if (effectiveSlug === 'full-access') {
                            // Existing subscriber switching cycle — show confirmation
                            setPendingCycleSwitch(c.id);
                            setShowCycleSwitchConfirm(true);
                            return;
                          }
                          // New subscriber — just toggle preview
                          setCycleId(c.id);
                        }}
                        className={`rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                          (cycleId === c.id && !pendingBillingCycle) || isPendingTarget
                            ? 'bg-primary-600 text-white shadow-sm'
                            : isCurrentCycle
                              ? 'text-gray-600 hover:text-primary-600'
                              : 'text-gray-600 hover:text-primary-600'
                        }`}
                      >
                        {c.label}
                        {isCurrentCycle && !pendingBillingCycle && <span className="ml-1 text-xs opacity-75">(current)</span>}
                        {isPendingTarget && <span className="ml-1 text-xs text-green-200">(pending)</span>}
                        {!isCurrentCycle && !isPendingTarget && effectiveSlug === 'full-access' && c.id === 'yearly' && (
                          <span className="ml-1.5 text-xs font-bold text-green-300">Save {pricePerUserMonthly > 0 ? Math.round((1 - pricePerUserYearly / pricePerUserMonthly) * 100) : 50}%</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {cycleId === 'yearly' && effectiveSlug !== 'full-access' && !pendingBillingCycle && (
                  <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-md
                    dark:text-green-300 dark:bg-green-500/15 dark:ring-1 dark:ring-green-400/40">
                    Save {pricePerUserMonthly > 0 ? Math.round((1 - pricePerUserYearly / pricePerUserMonthly) * 100) : 50}%
                  </span>
                )}
              </div>

              {/* User count selector */}
              <div className="mt-5">
                <label className="text-sm font-medium text-gray-700">Number of Telecallers</label>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    onClick={() => setNumUsers(Math.max(minUsers, numUsers - 1))}
                    disabled={numUsers <= minUsers}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiMinusSm className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={minUsers}
                    max={500}
                    value={numUsers}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) {
                        setNumUsers(Math.min(500, Math.max(minUsers, val)));
                      }
                    }}
                    onBlur={() => {
                      // Ensure valid value on blur (e.g. if field is empty)
                      if (!numUsers || numUsers < minUsers) setNumUsers(minUsers);
                      if (numUsers > 500) setNumUsers(500);
                    }}
                    className="flex h-10 w-16 items-center justify-center rounded-lg border border-gray-300 bg-white text-center text-lg font-bold text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    onClick={() => setNumUsers(Math.min(500, numUsers + 1))}
                    disabled={numUsers >= 500}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiPlusSm className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-500">telecaller{numUsers > 1 ? 's' : ''} + 1 admin</span>
                </div>
              </div>

              {/* Features */}
              <ul className="mt-6 grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {(fullAccessPlan ? getPlanFeatureLabels(fullAccessPlan) : FALLBACK_FULL_ACCESS_FEATURES).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                    <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Pricing + Action */}
            <div className="flex flex-col items-center justify-center gap-4 lg:min-w-[280px]">
              <div className="text-center">
                {isUpgradeMode ? (
                  /* Upgrade pricing breakdown */
                  <>
                    <div className="flex items-end justify-center gap-1">
                      <span className="text-5xl font-bold tabular-nums text-gray-900">
                        {inr(upgradeTotalAmount)}
                      </span>
                      <div className="mb-2 text-left">
                        <span className="text-xs font-medium text-gray-500">/upgrade</span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      {additionalUsers} additional telecaller{additionalUsers > 1 ? 's' : ''} × {inr(perUserPrice)}/user
                      {cycleId === 'yearly' && ` × ${upgradeRemainingMonths} mo`}
                    </p>
                    {coinDiscountAmount > 0 && (
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                        {inr(upgradeSubtotal)} − {appliedCoins.toLocaleString('en-IN')} coins ({inr(coinDiscountAmount)}) = {inr(upgradeDiscountedSubtotal)}
                      </p>
                    )}
                    {upgradeGstAmount > 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        {inr(upgradeDiscountedSubtotal)}
                        {upgradeGstCalc.gstType === 'cgst_sgst'
                          ? ` + ${inr(upgradeCgstAmount)} CGST + ${inr(upgradeSgstAmount)} SGST`
                          : ` + ${inr(upgradeGstAmount)} GST`}
                        {' = '}{inr(upgradeTotalAmount)}
                      </p>
                    )}

                    {/* Refer & Earn — use coins toward this upgrade */}
                    {coinBalance > 0 && (
                      <div className="mt-4 w-full max-w-[280px] rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-left">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                            Use your coins ({coinBalance.toLocaleString('en-IN')} available)
                          </span>
                          <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useCoins}
                              onChange={(e) => setUseCoins(e.target.checked)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Apply</span>
                          </label>
                        </div>
                        {useCoins && (
                          <p className="mt-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
                            Redeeming all {appliedCoins.toLocaleString('en-IN')} usable coins
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* Normal pricing display */
                  <>
                    <div className="flex items-end justify-center gap-1">
                      <span className="text-5xl font-bold tabular-nums text-gray-900">
                        {inr(perUserPrice)}
                      </span>
                      <div className="mb-2 text-left">
                        <span className="text-xs font-medium text-gray-500">/user</span>
                        <br />
                        <span className="text-xs font-medium text-gray-500">/month</span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {cycleId === 'yearly' ? (
                        <>
                          {inr(perUserPrice)}/user/mo × {numUsers} telecaller{numUsers > 1 ? 's' : ''} × 12 mo = <strong>{inr(subtotal)}/year</strong>
                        </>
                      ) : (
                        <>
                          {inr(perUserPrice)}/user × {numUsers} telecaller{numUsers > 1 ? 's' : ''} = <strong>{inr(subtotal)}/month</strong>
                        </>
                      )}
                    </p>
                    {gstAmount > 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        {gstType === 'cgst_sgst'
                          ? `+ ${inr(cgstAmount)} CGST + ${inr(sgstAmount)} SGST`
                          : `+ ${inr(gstAmount)} GST`}
                        {' = '}{inr(totalAmount)}/{cycleId === 'yearly' ? 'year' : 'month'}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Action button — four-way: current / upgrade / downgrade / subscribe */}
              {effectiveSlug === 'full-access' && numUsers === currentPaidSeats ? (
                <div className="w-full max-w-[240px] rounded-lg bg-primary-100 py-3 text-center text-sm font-semibold text-primary-700
                  dark:bg-primary-500/15 dark:text-primary-300 dark:ring-1 dark:ring-primary-400/40">
                  Current Plan
                </div>
              ) : effectiveSlug === 'full-access' && numUsers > currentPaidSeats ? (
                <button
                  onClick={handleSeatUpgrade}
                  disabled={processing}
                  className="w-full max-w-[240px] rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : `Upgrade · ${inr(upgradeTotalAmount)}`}
                </button>
              ) : effectiveSlug === 'full-access' && numUsers < currentPaidSeats ? (
                <button
                  onClick={handleScheduleDowngrade}
                  disabled={processing}
                  className="w-full max-w-[280px] rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : `Downgrade at Renewal · ${numUsers} telecaller${numUsers > 1 ? 's' : ''}`}
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/app/checkout?plan=full-access&cycle=${cycleId}&users=${numUsers}`)}
                  className="w-full max-w-[240px] rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-700 transition-all"
                >
                  Subscribe · {inr(totalAmount)}/{cycleId === 'yearly' ? 'yr' : 'mo'}
                </button>
              )}

              {/* Upgrade summary info box */}
              {isUpgradeMode && (
                <div className="w-full max-w-[280px] rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800
                  dark:bg-blue-500/10 dark:border-blue-400/30 dark:text-blue-300">
                  <p className="font-medium">Adding {additionalUsers} telecaller{additionalUsers > 1 ? 's' : ''} to your current {currentPaidSeats} seat{currentPaidSeats > 1 ? 's' : ''}.</p>
                  <p className="mt-1">New total: <strong>{numUsers} telecallers + 1 admin = {numUsers + 1} users</strong>.</p>
                  {cycleId === 'yearly' && (
                    <p className="mt-1">Prorated for {upgradeRemainingMonths} remaining month{upgradeRemainingMonths > 1 ? 's' : ''}.</p>
                  )}
                </div>
              )}

              {/* Downgrade summary info box */}
              {isDowngradeMode && (
                <div className="w-full max-w-[280px] rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800
                  dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-300">
                  <p className="font-medium">Reducing from {currentPaidSeats} to {numUsers} telecaller{numUsers > 1 ? 's' : ''}.</p>
                  <p className="mt-1">New total: <strong>{numUsers} telecallers + 1 admin = {numUsers + 1} users</strong>.</p>
                  <p className="mt-1">⚠️ Change takes effect at your next renewal date. Please deactivate excess telecallers before then.</p>
                </div>
              )}

              {/* Trial link */}
              {/* {effectiveSlug === 'free' && (
                <button
                  onClick={handleStartTrial}
                  disabled={processing}
                  className="w-full max-w-[240px] rounded-lg border border-primary-300 bg-white px-6 py-2.5 text-sm font-semibold text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  Start {serverPlans.find(p => p.slug === 'trial')?.trialDays || 5}-Day Free Trial
                </button>
              )} */}

              <p className="text-xs text-gray-400 text-center">
                {cycleId === 'yearly' ? 'Billed annually' : 'Billed monthly'} · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Free Plan Banner */}
      <div className="relative overflow-hidden rounded-xl border-2 border-green-300 bg-gradient-to-r from-green-50 via-white to-green-50 p-5 sm:p-8
        dark:border-green-500/60 dark:bg-none dark:bg-[radial-gradient(ellipse_at_top_right,rgba(34,197,94,0.35),rgba(20,83,45,0.88)_52%,rgba(2,6,23,0.98))]
        dark:!shadow-[0_0_55px_-12px_rgba(74,222,128,0.40)]">
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-green-100/60 dark:bg-green-500/25" />
        <div className="absolute -left-3 -bottom-3 h-16 w-16 rounded-full bg-green-100/40 dark:bg-emerald-500/20" />
        <div className="relative flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-10">
          {/* Left: Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700
              dark:bg-green-500/15 dark:text-green-300 dark:ring-1 dark:ring-green-400/40">
              <HiLightningBolt className="h-3.5 w-3.5" />
              {freePlan?.name || 'Free Forever'}
            </div>
            <h3 className="mt-3 text-2xl font-bold text-gray-900">{freePlan?.description || 'Start Free — No Credit Card Needed'}</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-lg">
              Try TeleValley with essential calling & lead tools. Upgrade anytime when your team grows.
            </p>
            <ul className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {(freePlan ? getPlanFeatureLabels(freePlan) : FALLBACK_FREE_FEATURES).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                  <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Price + Action */}
          <div className="flex flex-col items-center gap-3 sm:min-w-[180px]">
            <div className="text-center">
              <span className="text-5xl font-bold text-green-600 dark:text-green-400">
                {freePlan?.price?.monthly === 0 ? 'Free' : freePlan ? inr(freePlan.price?.monthly || 0) : 'Free'}
              </span>
              <p className="mt-1 text-xs text-gray-500">
                {freePlan?.limits?.users === -1 ? 'Unlimited Users' : freePlan?.limits?.users === 1 ? '1 User' : freePlan?.limits?.users ? `Up to ${freePlan.limits.users} Users` : '1 User'}
                {' · '}
                {freePlan?.limits?.contacts === -1 ? 'Unlimited Leads' : `Up to ${freePlan?.limits?.contacts || 150} Leads`}
              </p>
            </div>
            {effectiveSlug === 'free' ? (
              <div className="inline-flex items-center gap-1 rounded-lg bg-green-100 px-4 py-2 text-sm font-semibold text-green-700
                dark:bg-green-500/15 dark:text-green-300 dark:ring-1 dark:ring-green-400/40">
                <HiCheck className="h-4 w-4" />
                Your Plan
              </div>
            ) : pendingDowngradePlan ? (
              <div className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-4 py-2 text-sm font-semibold text-red-700
                dark:bg-red-500/15 dark:text-red-300 dark:ring-1 dark:ring-red-400/40">
                <HiClock className="h-4 w-4" />
                Downgrade Pending
              </div>
            ) : (
              <button
                onClick={() => setShowDowngradeConfirm(true)}
                className="w-full max-w-[200px] rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
              >
                Downgrade
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Payment History</h3>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <HiClock className="mx-auto h-8 w-8 mb-2" />
              <p className="text-sm">No payment history yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="pb-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="pb-2 text-left font-semibold text-gray-600">Plan</th>
                    <th className="pb-2 text-left font-semibold text-gray-600">Telecallers</th>
                    <th className="pb-2 text-left font-semibold text-gray-600">Cycle</th>
                    <th className="pb-2 text-left font-semibold text-gray-600">Expires</th>
                    <th className="pb-2 text-right font-semibold text-gray-600">Amount</th>
                    <th className="pb-2 text-right font-semibold text-gray-600">Status</th>
                    <th className="pb-2 text-right font-semibold text-gray-600">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => {
                    const breakdown = getPaymentBreakdown(payment);
                    const isExpanded = expandedPaymentId === payment._id;
                    const periodEnded = payment.status === 'expired' ||
                      (payment.currentPeriodEnd && new Date(payment.currentPeriodEnd) < new Date());
                    return (
                    <React.Fragment key={payment._id}>
                    <tr className={`border-b border-gray-100 ${isExpanded ? '' : 'last:border-0'}`}>
                      <td className="py-3 text-gray-700">
                        {new Date(payment.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 text-gray-700">
                        {payment.planId?.name || payment.notes?.planSlug || '—'}
                      </td>
                      <td className="py-3 text-gray-600">
                        {payment.notes?.numUsers ? `${payment.notes.numUsers} telecaller${payment.notes.numUsers > 1 ? 's' : ''}` : '—'}
                      </td>
                      <td className="py-3 text-gray-600">
                        {payment.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                      </td>
                      <td className="py-3 text-gray-600">
                        {payment.currentPeriodEnd
                          ? new Date(payment.currentPeriodEnd).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="py-3 text-right text-gray-900 font-medium">
                        <div>{payment.totalAmount ? inr(payment.totalAmount) : '—'}</div>
                        {breakdown.hasDetails && (
                          <button
                            type="button"
                            onClick={() => setExpandedPaymentId(isExpanded ? null : payment._id)}
                            aria-expanded={isExpanded}
                            className="mt-0.5 text-[11px] font-medium text-primary-600 hover:text-primary-700"
                          >
                            {isExpanded ? 'Hide breakdown' : breakdown.promoCode ? `Breakdown · ${breakdown.promoCode}` : 'Breakdown'}
                          </button>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <Badge
                          variant={
                            (payment.status === 'active' || payment.status === 'expired') ? 'success' :
                            payment.status === 'failed' ? 'danger' :
                            payment.status === 'pending' ? 'warning' : 'default'
                          }
                        >
                          {payment.status === 'active' ? 'Paid' :
                           payment.status === 'expired' ? 'Paid' :
                           payment.status === 'failed' ? 'Failed' :
                           payment.status === 'pending' ? 'Pending' : payment.status}
                        </Badge>
                        {periodEnded && (payment.status === 'active' || payment.status === 'expired') && (
                          <div className="mt-0.5 text-[11px] text-gray-400 whitespace-nowrap">Period ended</div>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="inline-flex items-center gap-3">
                          <a
                            href={`${paymentAPI.getInvoice(payment._id)}?token=${encodeURIComponent(localStorage.getItem('admin_auth_token') || '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-xs font-medium"
                          >
                            View
                          </a>
                          <a
                            href={`${paymentAPI.downloadInvoice(payment._id)}&token=${encodeURIComponent(localStorage.getItem('admin_auth_token') || '')}`}
                            download={`invoice-TV-${payment._id.toString().slice(-6).toUpperCase()}.pdf`}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-xs font-medium"
                          >
                            <HiDownload className="h-3.5 w-3.5" />
                            PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && breakdown.hasDetails && (
                      <tr className="border-b border-gray-100 last:border-0 bg-gray-50/60">
                        <td colSpan={8} className="px-3 py-3">
                          <dl className="ml-auto max-w-sm space-y-1 text-xs">
                            {breakdown.originalAmount != null && (
                              <div className="flex justify-between gap-6">
                                <dt className="text-gray-500">Plan price</dt>
                                <dd className="text-gray-800 font-medium">{inr(breakdown.originalAmount)}</dd>
                              </div>
                            )}
                            {breakdown.discountAmount > 0 && (
                              <div className="flex justify-between gap-6">
                                <dt className="text-gray-500">
                                  Promo discount{breakdown.promoCode ? <> (<span className="font-semibold text-gray-700">{breakdown.promoCode}</span>)</> : null}
                                </dt>
                                <dd className="text-green-700 font-medium">− {inr(breakdown.discountAmount)}</dd>
                              </div>
                            )}
                            {breakdown.taxableAmount != null && (
                              <div className="flex justify-between gap-6">
                                <dt className="text-gray-500">Taxable amount</dt>
                                <dd className="text-gray-800 font-medium">{inr(breakdown.taxableAmount)}</dd>
                              </div>
                            )}
                            {breakdown.cgstAmount > 0 || breakdown.sgstAmount > 0 ? (
                              <>
                                <div className="flex justify-between gap-6">
                                  <dt className="text-gray-500">CGST</dt>
                                  <dd className="text-gray-800 font-medium">{inr(breakdown.cgstAmount)}</dd>
                                </div>
                                <div className="flex justify-between gap-6">
                                  <dt className="text-gray-500">SGST</dt>
                                  <dd className="text-gray-800 font-medium">{inr(breakdown.sgstAmount)}</dd>
                                </div>
                              </>
                            ) : breakdown.gstAmount > 0 ? (
                              <div className="flex justify-between gap-6">
                                <dt className="text-gray-500">GST{breakdown.gstType === 'igst' ? ' (IGST)' : ''}</dt>
                                <dd className="text-gray-800 font-medium">{inr(breakdown.gstAmount)}</dd>
                              </div>
                            ) : null}
                            <div className="flex justify-between gap-6 pt-1 border-t border-gray-200">
                              <dt className="text-gray-700 font-semibold">Total paid</dt>
                              <dd className="text-gray-900 font-bold">{payment.totalAmount ? inr(payment.totalAmount) : '—'}</dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDowngradeConfirm(false)}>
          <div className="mx-4 max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <HiExclamation className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Downgrade to {freePlan?.name || 'Free Plan'}?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              You're about to downgrade from <strong>{effectivePlan?.name || currentPlanName || 'your current plan'}</strong> to the <strong>{freePlan?.name || 'Free Plan'}</strong>.
            </p>
            {subscriptionEndsAt ? (
              <p className="text-sm text-amber-700 mb-6">
                Your Full Access features will continue working until <strong>{new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>. After that, your plan will switch to Free. You can cancel this downgrade anytime before then.
              </p>
            ) : (
              <p className="text-sm text-red-600 mb-6">
                This will remove access to premium features immediately. This action cannot be undone.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDowngradeConfirm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDowngrade}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                {subscriptionEndsAt ? 'Schedule Downgrade' : 'Downgrade Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowUpgradeModal(false)}>
          <div className="mx-4 max-w-lg w-full rounded-2xl bg-white p-6 shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Upgrade Your Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="space-y-4">
              {upgradePlans.map((plan) => {
                const planFeatures = getPlanFeatureLabels(plan);
                const priceLabel = plan.slug === 'trial'
                  ? `Free for ${plan.trialDays || 5} day${plan.trialDays !== 1 ? 's' : ''}`
                  : plan.isPerUser
                    ? `₹${(plan.pricePerUser?.monthly || 0).toLocaleString('en-IN')}/user/mo`
                    : `₹${(plan.price?.monthly || 0).toLocaleString('en-IN')}/mo`;
                const yearlyLabel = plan.slug === 'trial'
                  ? null
                  : plan.isPerUser && plan.pricePerUser?.yearly
                    ? `₹${(plan.pricePerUser.yearly).toLocaleString('en-IN')}/user/mo yearly`
                    : !plan.isPerUser && plan.price?.yearly
                      ? `₹${(plan.price.yearly).toLocaleString('en-IN')}/mo yearly`
                      : null;
                return (
                  <div
                    key={plan.slug}
                    className={`relative rounded-xl border-2 p-5 transition-all ${
                      plan.isPopular
                        ? 'border-primary-400 bg-gradient-to-br from-white via-primary-50/30 to-white'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {plan.isPopular && (
                      <div className="absolute -top-0 right-4">
                        <div className="rounded-b-lg bg-primary-600 px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm">
                          <HiStar className="inline h-3 w-3 mr-0.5 -mt-0.5" />Best Value
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-bold text-gray-900">{plan.name}</h4>
                        {plan.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>
                        )}
                        <p className="text-base font-semibold text-gray-900 mt-1.5">{priceLabel}</p>
                        {yearlyLabel && (
                          <p className="text-xs text-green-600 font-medium">{yearlyLabel}</p>
                        )}
                        <div className="flex gap-2 mt-1 text-xs text-gray-500">
                          <span>{plan.limits?.users === -1 ? 'Unlimited Users' : plan.limits?.users === 1 ? '1 User' : `Up to ${plan.limits?.users} Users`}</span>
                          <span>·</span>
                          <span>{plan.limits?.contacts === -1 ? 'Unlimited Leads' : `Up to ${plan.limits?.contacts || 150} Leads`}</span>
                        </div>
                        {planFeatures.length > 0 && (
                          <ul className="mt-3 grid gap-x-3 gap-y-1 sm:grid-cols-2">
                            {planFeatures.slice(0, 8).map((f) => (
                              <li key={f} className="flex items-start gap-1.5 text-sm text-gray-600">
                                <HiCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" />
                                {f}
                              </li>
                            ))}
                            {planFeatures.length > 8 && (
                              <li className="text-sm text-primary-600 font-medium">+{planFeatures.length - 8} more</li>
                            )}
                          </ul>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setShowUpgradeModal(false);
                          navigate(`/app/checkout?plan=${plan.slug}&cycle=${cycleId}${(plan.isPerUser || plan.slug === 'full-access') ? `&users=${numUsers}` : ''}`);
                        }}
                        className="self-start sm:self-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors whitespace-nowrap"
                      >
                        Upgrade
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Cycle Switch Confirmation Modal */}
      {showCycleSwitchConfirm && pendingCycleSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCycleSwitchConfirm(false)}>
          <div className="mx-4 max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <HiClock className="h-5 w-5 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Switch to {pendingCycleSwitch === 'yearly' ? 'Yearly' : 'Monthly'} Billing?
              </h3>
            </div>

            {pendingCycleSwitch === 'yearly' ? (
              <>
                <p className="text-sm text-gray-600 mb-2">
                  You're switching from <strong>monthly</strong> to <strong>yearly</strong> billing for <strong>{currentPaidSeats} telecaller{currentPaidSeats > 1 ? 's' : ''}</strong>.
                </p>
                <div className="rounded-lg bg-green-50 border border-green-200 p-3 mb-4">
                  <p className="text-sm font-semibold text-green-700">
                    Save {pricePerUserMonthly > 0 ? Math.round((1 - pricePerUserYearly / pricePerUserMonthly) * 100) : 50}% per user!
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    New rate: {inr(pricePerUserYearly)}/user/mo (billed yearly)
                  </p>
                </div>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  The change will take effect at your next renewal on <strong>{subscriptionEndsAt ? new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'your next renewal date'}</strong>.
                  Until then, your current monthly billing continues unchanged.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">
                  You're switching from <strong>yearly</strong> to <strong>monthly</strong> billing for <strong>{currentPaidSeats} telecaller{currentPaidSeats > 1 ? 's' : ''}</strong>.
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  Switching to monthly billing means you'll lose the yearly discount. The change takes effect at your next renewal on <strong>{subscriptionEndsAt ? new Date(subscriptionEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'your next renewal date'}</strong>.
                  Your current yearly billing continues until then.
                </p>
              </>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCycleSwitchConfirm(false); setPendingCycleSwitch(null); }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCycleSwitch}
                disabled={processing}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Scheduling...' : `Schedule Switch to ${pendingCycleSwitch === 'yearly' ? 'Yearly' : 'Monthly'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Trust */}
      <div className="flex items-center justify-center gap-2 text-xs font-medium text-gray-500 py-2">
        <HiShieldCheck className="h-4 w-4 text-green-500" />
        Secure checkout via Razorpay · GST invoice included · UPI, Cards, Net Banking & Wallets · Cancel anytime
      </div>
    </div>
  );
};

export default Subscription;